import { parseISO } from "date-fns";
import axios from "@/common/services/axios";
import {
  GROUPS_ENDPOINT,
  PLAYERS_ENDPOINT,
  PLAYERS_ME_ENDPOINT,
  UPDATE_AVATAR_OPERATION,
} from "@/common/constants";
import {
  Group,
  GroupForm,
  GroupInfoRequest,
  GroupRequest,
  GroupResponse,
  GroupRulesForm,
  GroupRulesRequest,
  GroupSettingsForm,
  GroupSettingsRequest,
  GroupInvitationTokenResponse,
} from "@/common/models/group";
import { Competition } from "@/common/models/competition";
import { User, UserRequest } from "@/common/models/user";
import { Player, PlayerRole, UpdatePlayerResponse } from "@/common/models/player";

const processGroup = (raw: GroupResponse): Group => ({
  id: raw.id,
  user: raw.user_id,
  competition: raw.competition_id,
  players: [],
  rules: raw.rules,
  info: {
    name: raw.info.name,
    caption: raw.info.caption,
    description: raw.info.description,
    startDate: parseISO(raw.info.start_date),
    endDate: parseISO(raw.info.end_date),
  },
  settings: {
    avatar: raw.settings.avatar,
    theme: raw.settings.theme,
    background: raw.settings.background,
    chatActive: raw.settings.chat_active,
    public: raw.settings.public,
    resultVisible: raw.settings.result_visible,
    resultRepetition: raw.settings.result_repetition,
  },
  created: parseISO(raw.created_at),
  updated: parseISO(raw.updated_at),
  createdBy: raw.created_by,
  updatedBy: raw.updated_by,
  status: raw.status,
});

const processGroupForm = (data: GroupForm): Partial<GroupRequest> => ({
  info: {
    name: data.name,
    description: data.description,
  },
  settings: {
    result_visible: data.visible,
    result_repetition: data.repetition,
  },
});

const processInfoGroup = (data: GroupForm): Partial<GroupInfoRequest> => ({
  name: data.name,
  description: data.description,
});

const processSettingsGroup = (data: GroupSettingsForm): Partial<GroupSettingsRequest> => ({
  result_repetition: data.repetition,
  result_visible: data.visible,
});

const processRulesGroup = (data: GroupRulesForm[]): Partial<GroupRulesRequest>[] =>
  data.map(
    (v: GroupRulesForm): Partial<GroupRulesRequest> => ({
      rule_id: v.rule_id,
      priority: v.priority,
      data: v.data,
    }),
  );

const processPlayerRequest = (data: User[]): UserRequest[] => data.map((u) => ({ user_id: u.id }));

const proccessUpdatedPlayer = (response: UpdatePlayerResponse, oldPlayer: Player): Player => ({
  ...oldPlayer,
  roles: response.roles.map((r) => r as unknown as PlayerRole),
  created: parseISO(response.created_at),
  updated: parseISO(response.updated_at),
  createdBy: response.created_by,
  updatedBy: response.updated_by,
});

export const fetchMeGroups = async (): Promise<Group[]> => {
  const groupsResponse = await axios.get<GroupResponse[]>(`${GROUPS_ENDPOINT}`);
  return Array.isArray(groupsResponse.data) ? groupsResponse.data.map(processGroup) : [];
};

export const fetchGroupById = async (id: string): Promise<Group> => {
  const groupResponse = await axios.get<GroupResponse>(`${GROUPS_ENDPOINT}/${id}`);
  return processGroup(groupResponse.data);
};

export const fetchInvitationTokenGroup = async (id: string): Promise<string> => {
  const groupsResponse = await axios.get<GroupInvitationTokenResponse>(
    `${GROUPS_ENDPOINT}/${id}/invitation`,
  );
  return groupsResponse.data.invitation_code;
};

export const createGroup = async (group: GroupForm, competition: Competition): Promise<any> => {
  try {
    const groupDTO = processGroupForm(group);
    groupDTO.competition_id = competition.id;
    await axios.post(`${GROUPS_ENDPOINT}`, groupDTO);
  } catch (error) {
    console.error(error);
  }
};

export const exitGroup = async (group: Group): Promise<any> => {
  try {
    await axios.post(`${GROUPS_ENDPOINT}/${group.id}${PLAYERS_ME_ENDPOINT}:exitGroup`);
  } catch (error) {
    console.error(error);
  }
};

export const declinePlayer = (group: Group, player: Player): Promise<any> =>
  axios.post(`${GROUPS_ENDPOINT}/${group.id}${PLAYERS_ENDPOINT}/${player.id}:exitGroup`);

export const doPlayerAdmin = async (group: Group, player: Player): Promise<Player> => {
  const response = await axios.put<UpdatePlayerResponse>(
    `${GROUPS_ENDPOINT}/${group.id}${PLAYERS_ENDPOINT}/${player.id}`,
    {
      roles: ["ADMIN"],
    },
  );
  return proccessUpdatedPlayer(response.data, player);
};

export const editInfoGroup = async (group: GroupForm, id: string): Promise<any> => {
  if (group.avatar) {
    const formData = new FormData();
    formData.append("file", group.avatar);
    await axios.post<GroupResponse>(
      `${GROUPS_ENDPOINT}/${id}${UPDATE_AVATAR_OPERATION}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
  }
  const groupDTO = processInfoGroup(group);
  await axios.post(`${GROUPS_ENDPOINT}/${id}:updateInfo`, groupDTO);
};

export const editSettingsGroup = async (group: GroupSettingsForm, id: string): Promise<any> => {
  try {
    const groupDTO = processSettingsGroup(group);
    await axios.post(`${GROUPS_ENDPOINT}/${id}:updateSettings`, groupDTO);
  } catch (error) {
    console.error(error);
  }
};

export const editRulesGroup = async (groupRules: GroupRulesForm[], id: string): Promise<any> => {
  try {
    const groupRulesDTO = processRulesGroup(groupRules);
    await axios.post(`${GROUPS_ENDPOINT}/${id}:updateRules`, groupRulesDTO);
  } catch (error) {
    console.error(error);
  }
};

export const createNewPlayer = async (users: User[], group: Group): Promise<any> => {
  try {
    const usersRequests = processPlayerRequest(users);
    await axios.post(`${GROUPS_ENDPOINT}/${group.id}:addPlayers`, usersRequests);
  } catch (error) {
    console.error(error);
  }
};

export const joinGroup = async (tokenInvitation: string): Promise<any> => {
  await axios.post(`${GROUPS_ENDPOINT}:join`, {
    invitation_code: tokenInvitation,
  });
};
